/**
 * CategoryCard - Compact row for exposure alert categories.
 *
 * Displays a security category in a single horizontal row:
 * - Risk level badge (inline, left)
 * - Category name (inline)
 * - Description (inline, secondary color, hidden on mobile)
 * - Selection checkbox (right)
 *
 * Used in:
 * - CategoryExposureAlertsForm (single-column list with dividers)
 */

import type { TagSeverity } from '@praetorian-chariot/ui';
import { Checkbox, RiskTag } from '@praetorian-chariot/ui';

import { cn } from '@/utils/classname';

import type {
  ExposureCategory,
  ExposureCategoryRisk,
} from './exposureAlertCategories';

interface CategoryCardProps {
  /** Category to display */
  category: ExposureCategory;
  /** Whether the category is currently selected */
  selected: boolean;
  /** Called when the card is clicked to toggle selection */
  onToggle: () => void;
  /** Whether the card is disabled */
  disabled?: boolean;
}

/**
 * Maps exposure category risk levels to TagSeverity for RiskTag component.
 */
const riskToSeverity: Record<ExposureCategoryRisk, TagSeverity> = {
  critical: 'critical',
  high: 'high',
  medium: 'medium',
};

/**
 * CategoryCard provides a compact horizontal row interface for security categories.
 *
 * Visual states:
 * - Unselected: No background, gray checkbox
 * - Selected: Subtle brand background, filled checkbox with checkmark
 * - Hover: Secondary background
 * - Disabled: Reduced opacity, no interactions
 *
 * @example
 * ```tsx
 * <CategoryCard
 *   category={remoteAccessCategory}
 *   selected={selectedCategories.has('remote-access')}
 *   onToggle={() => toggleCategory('remote-access')}
 * />
 * ```
 */
export function CategoryCard({
  category,
  selected,
  onToggle,
  disabled = false,
}: CategoryCardProps) {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={cn('group contents', disabled && 'pointer-events-none')}
      data-testid={`category-${category.id}`}
    >
      {/* Column 1: Severity badge (5rem/80px fixed) */}
      <div
        className={cn(
          'flex h-12 items-center px-4',
          'group-hover:bg-chariot-background-secondary transition-colors',
          selected && 'bg-chariot-brand-primary/5',
          disabled && 'opacity-50'
        )}
      >
        <RiskTag severity={riskToSeverity[category.risk]} size="xs" />
      </div>

      {/* Column 2: Title + Description (1fr flexible) */}
      <div
        className={cn(
          'flex h-12 items-center gap-2 overflow-hidden pl-4',
          'group-hover:bg-chariot-background-secondary transition-colors',
          selected && 'bg-chariot-brand-primary/5',
          disabled && 'opacity-50'
        )}
      >
        <span className="text-chariot-text-primary truncate text-sm font-medium">
          {category.name}
        </span>
        <span className="text-chariot-text-secondary hidden truncate text-xs sm:inline">
          - {category.description}
        </span>
      </div>

      {/* Column 3: Checkbox (3rem/48px fixed) */}
      <div
        className={cn(
          'flex h-12 items-center justify-center px-2',
          'group-hover:bg-chariot-background-secondary transition-colors',
          selected && 'bg-chariot-brand-primary/5',
          disabled && 'opacity-50'
        )}
      >
        <Checkbox isChecked={selected} disabled={disabled} />
      </div>
    </button>
  );
}
